import axios from 'axios';
import { useState } from 'react';

import type { ContentElement, ImageUrls, PublishState } from '../types/news';
import { getFileExtension, uploadImage } from '../utils/imageUpload';

/**
 * Constructs an article object ready for persistence.
 */
function buildArticle(fields: {
  title: string;
  selectedCategory: string | null;
  shortDescription: string;
  noCoverImage: boolean;
  coverImageUrls: ImageUrls | null;
  elements: ContentElement[];
}) {
  const {
    title,
    selectedCategory,
    shortDescription,
    noCoverImage,
    coverImageUrls,
    elements,
  } = fields;

  return {
    id: crypto.randomUUID(),
    title,
    category: selectedCategory,
    shortDescription,
    coverImage: noCoverImage ? null : coverImageUrls,
    content: elements
      .filter((el) => el.type !== 'image' || el.urls !== null)
      .map((el) => {
        if (el.type === 'image') {
          const { uploading, ...rest } = el;
          return rest;
        }
        return el;
      }),
    status: 'draft',
    published: false,
    timestamps: {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: null,
    },
  };
}

/**
 * Hook managing the state and actions for the news article creation form.
 */
export function useNewsEditor() {
  const [elements, setElements] = useState<ContentElement[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [shortDescription, setShortDescription] = useState('');
  const [coverImageUrls, setCoverImageUrls] = useState<ImageUrls | null>(null);
  const [coverImageUploading, setCoverImageUploading] = useState(false);
  const [noCoverImage, setNoCoverImage] = useState(false);
  const [title, setTitle] = useState('');
  const [lastSavedId, setLastSavedId] = useState<string | null>(null);
  const [publishState, setPublishState] = useState<PublishState>({
    status: 'idle',
  });

  const moveElement = (index: number, direction: 'up' | 'down') => {
    const updated = [...elements];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= updated.length) return;
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    setElements(updated);
  };

  const isValid =
    title.trim() !== '' &&
    shortDescription.trim() !== '' &&
    selectedCategory !== null &&
    (noCoverImage || coverImageUrls !== null);

  async function handleCoverImageUpload(file: File): Promise<void> {
    setCoverImageUploading(true);
    try {
      const filename = `images/cover/${crypto.randomUUID()}.${getFileExtension(file.name)}`;
      setCoverImageUrls(await uploadImage(filename, file));
    } catch (err) {
      console.error('Cover-Upload fehlgeschlagen', err);
    } finally {
      setCoverImageUploading(false);
    }
  }

  function handleToggleNoCoverImage(): void {
    setNoCoverImage((prev) => !prev);
    setCoverImageUrls(null);
  }

  async function handleImageUpload(id: string, file: File): Promise<void> {
    updateElement(id, { uploading: true });
    try {
      const filename = `images/${crypto.randomUUID()}.${getFileExtension(file.name)}`;
      const urls = await uploadImage(filename, file);
      updateElement(id, { urls, uploading: false });
    } catch (err) {
      console.error('Bild-Upload fehlgeschlagen', err);
      updateElement(id, { uploading: false });
    }
  }

  function updateElement(id: string, updates: Partial<ContentElement>): void {
    setElements((prev) =>
      prev.map((el) =>
        el.id === id ? ({ ...el, ...updates } as ContentElement) : el,
      ),
    );
  }

  function deleteElement(id: string): void {
    setElements((prev) => prev.filter((el) => el.id !== id));
  }

  function addTextSection(): void {
    setElements((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'text',
        hasSubtitle: false,
        subtitle: '',
        text: '',
      },
    ]);
  }

  function addLinkSection(): void {
    setElements((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'link',
        linkDescription: '',
        url: '',
        showedText: '',
      },
    ]);
  }

  function addImageSection(): void {
    setElements((prev) => [
      ...prev,
      { id: crypto.randomUUID(), type: 'image', urls: null, uploading: false },
    ]);
  }

  async function handlePublish(): Promise<void> {
    setPublishState({ status: 'loading' });
    try {
      const article = buildArticle({
        title,
        selectedCategory,
        shortDescription,
        noCoverImage,
        coverImageUrls,
        elements,
      });

      const { data } = await axios.post<{
        uploadUrl: string;
        publicUrl: string;
      }>('http://localhost:3001/upload', {
        filename: `blogs/${article.id}.json`,
        contentType: 'application/json',
      });

      await axios.put(data.uploadUrl, JSON.stringify(article), {
        headers: { 'Content-Type': 'application/json' },
      });

      setLastSavedId(article.id);
      setPublishState({ status: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setPublishState({ status: 'error', message });
    }
  }

  return {
    elements,
    title,
    setTitle,
    selectedCategory,
    setSelectedCategory,
    shortDescription,
    setShortDescription,
    coverImageUrls,
    coverImageUploading,
    noCoverImage,
    moveElement,
    publishState,
    isValid,
    lastSavedId,
    handleCoverImageUpload,
    handleToggleNoCoverImage,
    handleImageUpload,
    updateElement,
    deleteElement,
    addTextSection,
    addLinkSection,
    addImageSection,
    handlePublish,
  };
}
